import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/shared/Button';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { PlayerNameButton } from '@/components/shared/PlayerNameButton';
import { Panel } from '@/components/shared/Panel';
import { ResultImageLightbox } from '@/components/events/ResultImageLightbox';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import { useStickyAutoScroll } from '@/hooks/useStickyAutoScroll';
import {
  deleteEventResultAsset,
  listEventDiscussionMessages,
  listEventResultAssets,
  sendEventDiscussionMessage,
  updateEventResultAssetCaption,
  uploadEventResultAsset,
} from '@/lib/repositories/chatRepository';
import { supabase } from '@/lib/supabase/client';
import type { EventDiscussionMessage, EventResultAsset } from '@/types/domain';
import { formatDateTime } from '@/utils/date';
import { getProfileInGameName } from '@/utils/profile';

interface EventDiscussionPanelProps {
  eventId: string;
  title: string;
  canUploadResults: boolean;
  focusMessageId?: string;
  focusResultId?: string;
  onMessageFocusConsumed?: (messageId: string) => void;
  onResultFocusConsumed?: (resultId: string) => void;
}

interface MessageRow extends EventDiscussionMessage {
  senderName: string;
  senderDisplayName?: string;
  isOwnMessage: boolean;
}

interface AssetRow extends EventResultAsset {
  uploaderName: string;
}

function formatTime(value: string): string {
  const fallback = formatDateTime(value, 'Time unavailable');
  const timestamp = new Date(value).getTime();

  if (!Number.isFinite(timestamp)) {
    return fallback;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

export function EventDiscussionPanel({
  eventId,
  title,
  canUploadResults,
  focusMessageId,
  focusResultId,
  onMessageFocusConsumed,
  onResultFocusConsumed,
}: EventDiscussionPanelProps): JSX.Element {
  const { profile } = useAuth();
  const { state } = useGuildData();
  const [messages, setMessages] = useState<EventDiscussionMessage[]>([]);
  const [assets, setAssets] = useState<EventResultAsset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [caption, setCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInputResetKey, setFileInputResetKey] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [editingCaption, setEditingCaption] = useState('');
  const [isSavingCaption, setIsSavingCaption] = useState(false);
  const [pendingDeleteAsset, setPendingDeleteAsset] = useState<EventResultAsset | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeletingAsset, setIsDeletingAsset] = useState(false);
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [highlightedResultId, setHighlightedResultId] = useState<string | null>(null);
  const consumedMessageFocusRef = useRef<string | null>(null);
  const consumedResultFocusRef = useRef<string | null>(null);

  const messageRows = useMemo<MessageRow[]>(
    () =>
      messages.map((message) => {
        const sender = state.profiles.find((entry) => entry.id === message.profileId);

        return {
          ...message,
          senderName: getProfileInGameName(sender),
          senderDisplayName: sender?.displayName,
          isOwnMessage: message.profileId === profile?.id,
        };
      }),
    [messages, profile?.id, state.profiles],
  );

  const assetRows = useMemo<AssetRow[]>(
    () =>
      assets.map((asset) => {
        const uploader = state.profiles.find((entry) => entry.id === asset.uploaderProfileId);

        return {
          ...asset,
          uploaderName: getProfileInGameName(uploader),
        };
      }),
    [assets, state.profiles],
  );

  const { containerRef, hasDetachedUpdates, pinNextUpdate, scrollToBottom } = useStickyAutoScroll<HTMLDivElement>({
    dependencyKey: `${messageRows.length}:${isLoading ? 'loading' : 'ready'}`,
  });

  useEffect(() => {
    let isActive = true;

    const loadDiscussion = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [nextMessages, nextAssets] = await Promise.all([listEventDiscussionMessages(eventId), listEventResultAssets(eventId)]);

        if (!isActive) {
          return;
        }

        setMessages(nextMessages);
        setAssets(nextAssets);
      } catch (nextError) {
        if (!isActive) {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : 'Failed to load the mass discussion.');
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadDiscussion();

    return () => {
      isActive = false;
    };
  }, [eventId]);

  useEffect(() => {
    if (!supabase) {
      return undefined;
    }

    const client = supabase;
    const channel = client
      .channel(`event-discussion-${eventId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'event_discussion_messages', filter: `event_id=eq.${eventId}` },
        () => {
          void listEventDiscussionMessages(eventId).then(setMessages).catch(() => undefined);
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'event_result_assets', filter: `event_id=eq.${eventId}` },
        () => {
          void listEventResultAssets(eventId).then(setAssets).catch(() => undefined);
        },
      )
      .subscribe();

    return () => {
      void client.removeChannel(channel);
    };
  }, [eventId]);

  useEffect(() => {
    if (selectedAssetIndex === null) {
      return;
    }

    if (assetRows.length === 0) {
      setSelectedAssetIndex(null);
      return;
    }

    if (selectedAssetIndex >= assetRows.length) {
      setSelectedAssetIndex(assetRows.length - 1);
    }
  }, [assetRows.length, selectedAssetIndex]);

  const selectedAsset = selectedAssetIndex === null ? null : assetRows[selectedAssetIndex] ?? null;

  useEffect(() => {
    if (!focusMessageId) {
      consumedMessageFocusRef.current = null;
    }
  }, [focusMessageId]);

  useEffect(() => {
    if (!focusResultId) {
      consumedResultFocusRef.current = null;
    }
  }, [focusResultId]);

  useEffect(() => {
    if (!focusMessageId || messageRows.length === 0 || consumedMessageFocusRef.current === focusMessageId) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(`discussion-message-${focusMessageId}`);

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedMessageId(focusMessageId);
        consumedMessageFocusRef.current = focusMessageId;
        onMessageFocusConsumed?.(focusMessageId);
        window.setTimeout(() => setHighlightedMessageId((current) => (current === focusMessageId ? null : current)), 2400);
      }
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [focusMessageId, messageRows.length, onMessageFocusConsumed]);

  useEffect(() => {
    if (!focusResultId || assetRows.length === 0 || consumedResultFocusRef.current === focusResultId) {
      return undefined;
    }

    const targetIndex = assetRows.findIndex((asset) => asset.id === focusResultId);

    if (targetIndex < 0) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(`result-asset-${focusResultId}`);

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedResultId(focusResultId);
        consumedResultFocusRef.current = focusResultId;
        onResultFocusConsumed?.(focusResultId);
        window.setTimeout(() => setHighlightedResultId((current) => (current === focusResultId ? null : current)), 2400);
      }
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [assetRows, focusResultId, onResultFocusConsumed]);

  const handleUpload = () => {
    if (!selectedFile || !profile) {
      return;
    }

    setIsUploading(true);
    setError(null);

    void uploadEventResultAsset(eventId, profile.id, selectedFile, caption)
      .then(() => {
        setSelectedFile(null);
        setCaption('');
        setFileInputResetKey((current) => current + 1);
      })
      .catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : 'Failed to upload the result image.');
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleSendMessage = () => {
    if (!profile || !draft.trim()) {
      return;
    }

    setIsSending(true);
    setError(null);
    pinNextUpdate();

    void sendEventDiscussionMessage(eventId, profile.id, draft)
      .then(() => {
        setDraft('');
      })
      .catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : 'Failed to send the discussion message.');
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const handleSaveCaption = (assetId: string) => {
    setIsSavingCaption(true);
    setError(null);

    void updateEventResultAssetCaption(assetId, editingCaption)
      .then(() => {
        setEditingAssetId(null);
        setEditingCaption('');
      })
      .catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : 'Failed to save the caption.');
      })
      .finally(() => {
        setIsSavingCaption(false);
      });
  };

  const handleDeleteAsset = () => {
    if (!pendingDeleteAsset) {
      return;
    }

    setIsDeletingAsset(true);
    setError(null);

    void deleteEventResultAsset(pendingDeleteAsset.id)
      .then(() => {
        setPendingDeleteAsset(null);
        setDeleteConfirmation('');
      })
      .catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : 'Failed to delete the result image.');
      })
      .finally(() => {
        setIsDeletingAsset(false);
      });
  };

  return (
    <Panel title="Finished Mass Discussion" description={`Discussion, killboard uploads, and result review for ${title}.`}>
      {isLoading ? <p className="copy-block">Loading discussion...</p> : null}
      {!isLoading && error ? <p className="form-footnote form-footnote--error">{error}</p> : null}

      {!isLoading ? (
        <>
          {canUploadResults ? (
            <div className="event-discussion__upload">
              <div className="form-grid form-grid--two">
                <label className="field">
                  <span className="field__label">Upload Killboard Image</span>
                  <input
                    key={fileInputResetKey}
                    className="field__input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
                  />
                </label>
                <label className="field">
                  <span className="field__label">Caption</span>
                  <input
                    className="field__input"
                    value={caption}
                    onChange={(event) => setCaption(event.target.value)}
                    placeholder="Optional result note"
                  />
                </label>
              </div>
              <div className="panel-actions">
                <Button variant="secondary" disabled={!selectedFile || isUploading} onClick={handleUpload}>
                  {isUploading ? 'Uploading...' : 'Upload Killboard Image'}
                </Button>
                {selectedFile ? <p className="copy-block">Selected file: {selectedFile.name}</p> : null}
              </div>
            </div>
          ) : null}

          {assetRows.length > 0 ? (
            <div className="event-results-grid">
              {assetRows.map((asset, index) => (
                <figure
                  key={asset.id}
                  id={`result-asset-${asset.id}`}
                  className={`event-results-grid__item${highlightedResultId === asset.id ? ' event-results-grid__item--highlighted' : ''}`}
                >
                  <button
                    type="button"
                    className="event-results-grid__viewer"
                    onClick={() => {
                      setSelectedAssetIndex(index);
                    }}
                  >
                    <img src={asset.imageUrl} alt={asset.caption ?? 'Mass result image'} loading="lazy" />
                  </button>

                  {editingAssetId === asset.id ? (
                    <div className="event-results-grid__editor">
                      <label className="field">
                        <span className="field__label">Caption</span>
                        <input
                          className="field__input"
                          value={editingCaption}
                          onChange={(event) => setEditingCaption(event.target.value)}
                          placeholder="Optional result note"
                        />
                      </label>
                      <div className="panel-actions">
                        <Button variant="secondary" disabled={isSavingCaption} onClick={() => handleSaveCaption(asset.id)}>
                          {isSavingCaption ? 'Saving...' : 'Save Caption'}
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={isSavingCaption}
                          onClick={() => {
                            setEditingAssetId(null);
                            setEditingCaption('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <figcaption className="event-results-grid__meta">
                        <strong>{asset.caption || 'Killboard / result image'}</strong>
                        <span>
                          Uploaded by {asset.uploaderName} - {formatDateTime(asset.createdAt)}
                        </span>
                      </figcaption>

                      {canUploadResults ? (
                        <div className="panel-actions">
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setEditingAssetId(asset.id);
                              setEditingCaption(asset.caption ?? '');
                            }}
                          >
                            Edit Caption
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              setPendingDeleteAsset(asset);
                              setDeleteConfirmation('');
                            }}
                          >
                            Delete Image
                          </Button>
                        </div>
                      ) : null}
                    </>
                  )}
                </figure>
              ))}
            </div>
          ) : (
            <p className="copy-block">No result images uploaded yet.</p>
          )}

          <div ref={containerRef} className="event-discussion__messages">
            {messageRows.length === 0 ? <p className="copy-block">No discussion yet.</p> : null}
            {messageRows.map((message) => (
              <article
                key={message.id}
                id={`discussion-message-${message.id}`}
                className={`event-discussion__message ${message.isOwnMessage ? 'event-discussion__message--own' : ''}${
                  highlightedMessageId === message.id ? ' event-discussion__message--highlighted' : ''
                }`}
              >
                <div className="event-discussion__message-meta">
                  <PlayerNameButton
                    profileId={message.profileId}
                    primaryLabel={message.senderName}
                    secondaryLabel={message.senderDisplayName}
                    compact
                  />
                  <span>{formatTime(message.createdAt)}</span>
                </div>
                <p>{message.content}</p>
              </article>
            ))}
          </div>

          {hasDetachedUpdates ? (
            <Button variant="ghost" className="guild-chat__jump-button" onClick={scrollToBottom}>
              Jump to latest discussion
            </Button>
          ) : null}

          <form
            className="event-discussion__composer"
            onSubmit={(event) => {
              event.preventDefault();
              handleSendMessage();
            }}
          >
            <textarea
              className="field__input field__input--textarea"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Discuss regroup notes, killboard takeaways, or finished-mass follow-up..."
            />
            <Button type="submit" disabled={isSending || !draft.trim()}>
              {isSending ? 'Sending...' : 'Send Discussion Message'}
            </Button>
          </form>
        </>
      ) : null}

      <ConfirmationDialog
        open={Boolean(pendingDeleteAsset)}
        title="Delete Result Image"
        description="Delete this result image from the finished mass gallery? This removes it from the results area for everyone."
        confirmLabel="Delete Image"
        confirmVariant="danger"
        confirmationLabel="Type DELETE to confirm"
        requiredText="DELETE"
        confirmationValue={deleteConfirmation}
        onConfirmationValueChange={setDeleteConfirmation}
        isProcessing={isDeletingAsset}
        onClose={() => {
          if (isDeletingAsset) {
            return;
          }

          setPendingDeleteAsset(null);
          setDeleteConfirmation('');
        }}
        onConfirm={handleDeleteAsset}
      />

      {selectedAsset ? (
        <ResultImageLightbox
          asset={selectedAsset}
          currentIndex={selectedAssetIndex ?? 0}
          totalCount={assetRows.length}
          onClose={() => setSelectedAssetIndex(null)}
          onPrevious={
            assetRows.length > 1
              ? () => setSelectedAssetIndex((current) => (current === null ? null : (current - 1 + assetRows.length) % assetRows.length))
              : undefined
          }
          onNext={
            assetRows.length > 1
              ? () => setSelectedAssetIndex((current) => (current === null ? null : (current + 1) % assetRows.length))
              : undefined
          }
        />
      ) : null}
    </Panel>
  );
}
